import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import { Platform, StyleSheet } from 'react-native';
import {
  Easing,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigationContainerRef } from 'expo-router';
import type { NoticeBannersStack } from '@sg/garnish';
import {
  NOTICE_BANNER_PADDING,
  theme,
  useModalContext,
  useResponsive,
} from '@sg/garnish';

import { NAVBAR_HEADER_HEIGHT } from '@order/navigation';
import { useUsableNetBannerOffset } from '@order/usable-net';

import { useActiveScreenOptions } from '../../hooks';
import { checkIfPresentedAsModalWithoutHeader } from '../../navigation';
import { NoticeBannersOverlayStack } from './components';

// ────────────────────────────────────────────────────────────────────────────────

export const NoticeBannersOverlay = () => {
  const safeAreaInsets = useSafeAreaInsets();
  const modalContext = useModalContext();
  const { currentBreakpoint } = useResponsive();
  const isPresentedFromBottom = currentBreakpoint.isXS;

  const activeScreenOptions = useActiveScreenOptions();
  const activeExpoScreenOptions = useSafeExpoScreenOptions();

  const isPresenetedAsModalWithoutHeader = checkIfPresentedAsModalWithoutHeader(
    activeExpoScreenOptions ?? activeScreenOptions,
  );

  // To identify whether the active screen is presented as a `Modal`
  // or whether a `Modal` component (garnish) is open.
  const isFullScreenModal =
    isPresenetedAsModalWithoutHeader || modalContext?.hasActiveModal === true;

  // ─── Offset & Animation ──────────────────────────────────────────

  const usableNetBannerOffset = useUsableNetBannerOffset();
  const noticeBannerOffset = useDerivedValue(
    () => withTiming(NAVBAR_HEADER_HEIGHT + usableNetBannerOffset, ANIMATION),
    [NAVBAR_HEADER_HEIGHT, usableNetBannerOffset],
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: noticeBannerOffset.value }],
  }));

  const offsetStyle = isPresentedFromBottom ? { bottom: 0 } : { top: 0 };
  const shouldAnimateOffset = !isPresentedFromBottom && !isFullScreenModal;
  const offsetAnimation = shouldAnimateOffset ? animatedStyle : undefined;

  // ─────────────────────────────────────────────────────────────────

  const bannerPadding = useMemo<BannerPaddings>(() => {
    const safeAreaTopInset = safeAreaInsets.top;
    const safeAreaBottomInset = safeAreaInsets.bottom;

    if (isPresentedFromBottom) {
      return {
        ...NOTICE_BANNER_PADDING,
        bottom: Platform.select({
          android: safeAreaBottomInset + NOTICE_BANNER_PADDING.bottom,
          default: Math.max(
            NOTICE_BANNER_PADDING.bottom,
            safeAreaBottomInset + theme.spacing['2'],
          ),
        }),
      };
    }

    return {
      ...NOTICE_BANNER_PADDING,
      top: Math.max(
        NOTICE_BANNER_PADDING.top,
        safeAreaTopInset + theme.spacing['2'],
      ),
    };
  }, [isPresentedFromBottom, safeAreaInsets.bottom, safeAreaInsets.top]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <NoticeBannersOverlayStack
      bannerPadding={bannerPadding}
      pointerEvents="box-none"
      style={[
        styles.stack,
        StyleSheet.absoluteFill,
        offsetStyle,
        offsetAnimation,
      ]}
    />
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  stack: {
    zIndex: Platform.select({
      default: theme.zIndex.overModal,
      ios: undefined, // On iOS, we use the `FullWindowOverlay ` component which conflicts with `zIndex`.
    }),
  },
});

// ─── Hooks ───────────────────────────────────────────────────────────────────

const useSafeExpoScreenOptions = () => {
  try {
    const navigationRef = useNavigationContainerRef();

    if (!navigationRef.isReady()) return;

    return navigationRef.getCurrentOptions();
  } catch {
    return undefined;
  }
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ANIMATION = { duration: 500, easing: Easing.out(Easing.exp) };

// ─── Types ───────────────────────────────────────────────────────────────────

type BannerPaddings = ComponentProps<
  typeof NoticeBannersStack
>['bannerPadding'];
